
import React from 'react';
import { rem_to_px } from "../helper/px";
import { XAxisRt } from "../helper/chart/x-axis-rt";
import { YAxisRt } from "../helper/chart/y-axis-rt";
import {identity_matrix} from "../../lib/matrix";
import {sprintf} from "sprintf-js";
import {dollarToString} from "../../lib/currency-utils";

const mathjs = require("mathjs");

const { DenseMatrix } = mathjs;

const VIEW_OP = {
  NONE: "NONE",
  SCALE: "SCALE",
  PAN: "PAN",
}

const lerp = (t, from_val, to_val) => {
  return (1-t) * from_val + t * to_val;
}

const calc_dist = (x_0, y_0, x_1, y_1) => {
  return Math.sqrt((x_0 - x_1) ** 2 + (y_0 - y_1) ** 2);
}

// Convert to RGB, and adjust if selected
const calc_color = (str, selected) => {
  let r = parseInt(str.substring(1, 2), 16) * 16;
  let g = parseInt(str.substring(2, 3), 16) * 16;
  let b = parseInt(str.substring(3, 4), 16) * 16;
  const value = Math.max(r, g, b);
  if (selected) {
    const mult = value > 128 ? 0.5 : 1.5;
    r *= mult;
    g *= mult;
    b *= mult;
  }
  return sprintf("rgb(%d,%d,%d)", r, g, b);
}


// data: {
//  color: {String} // #HEX
//  labcl: {String}
//  data: [float]
//}

class ChartRt extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      mouse_x: 0,
      mouse_y: 0,
      view_matrix: identity_matrix().toJSON(),
      view_matrix_inv: identity_matrix().toJSON(),
      view_op: VIEW_OP.NONE,
      span_select_0: [],
      show_time_items: true,
    }

    this.updateSize(props);
    this.grabbed_pos = {
      x: 0,
      y: 0,
    }
  }

  updateSize = props => {
    this.width_px = props.width_px;
    this.height_px = props.height_px;
  }

  xAxisHeightPx = () => {
    return rem_to_px(this.props.xAxisHeightRem);
  }

  yAxisWidthPx = () => {
    return rem_to_px(this.props.yAxisWidthRem);
  }

  topAxisHeightPx = () => {
    return rem_to_px(this.props.topAxisHeightRem);
  }

  componentDidMount = () => {
    const axisHeightPx = this.xAxisHeightPx();
    const axisWidthPx = this.yAxisWidthPx();
    const topAxisHeightPx = this.topAxisHeightPx();
    const group_index = this.props.group_index;

    this.xAxis = new XAxisRt(() => {
      return {
        left: axisWidthPx,
        right: this.width_px,
        bottom: this.height_px,
        top: this.height_px - axisHeightPx,
      }
    }, this.props.sim_days);

    this.yAxis = new YAxisRt(() => {
      return {
        left: 0,
        right: axisWidthPx,
        top: topAxisHeightPx,
        bottom: this.height_px - axisHeightPx,
        canvas_width: this.width_px,
        canvas_height: this.height_px,
      }
    }, this.props.group_data[group_index].data, this.props.y_labels);

    this.updateCanvas();
    this.refs.canvas.tabIndex = 1000;
    this.refs.canvas.addEventListener("keydown", this.onKeyDown);
  }

  componentDidUpdate = () => {
    this.updateSize(this.props);
    this.updateCanvas();
    const num_spans = this.props.spans ? this.props.spans.length : 0;
    const arr = new Array(num_spans);
    arr.fill(false);
    if (this.state.span_select_0.length !== arr.length) {
      this.setState({
        span_select_0: arr,
      })
    }
  };

  renderOutline = () => {
    const axisWidthPx = this.yAxisWidthPx();
    const topAxisHeightPx = this.topAxisHeightPx();

    const canvas = this.refs.canvas;
    const ctx = canvas.getContext("2d");

    ctx.strokeStyle = "#000";
    ctx.lineWidth = 0.5;
    ctx.moveTo(axisWidthPx, topAxisHeightPx);
    ctx.lineTo(this.width_px, topAxisHeightPx);
    ctx.stroke();
  }

  genClipPathGraph = () => {
    const clip_path = new Path2D();
    clip_path.rect(this.yAxisWidthPx(), this.topAxisHeightPx(),
      this.width_px - this.yAxisWidthPx(), this.height_px - this.topAxisHeightPx() - this.xAxisHeightPx());
    return clip_path;
  }

  genClipPathTopMargin = () => {
    const clip_path = new Path2D();
    clip_path.rect(this.yAxisWidthPx(), 0,
      this.width_px - this.yAxisWidthPx(), this.height_px - this.topAxisHeightPx());
    return clip_path;
  }

  renderData = () => {
    const canvas = this.refs.canvas;
    const ctx = canvas.getContext("2d");
    ctx.save();

    // Clip region
    ctx.clip(this.genClipPathGraph());

    const mouse_x = this.state.mouse_x;
    const mouse_y = this.state.mouse_y;
    const select_radius = 5;
    const selection = {
      label: "",
      date: "",
      y: 0,
    };

    const data = this.props.group_data[this.props.group_index].data;
    let selected = false;
    data.forEach(({color, data, label, lineWidth}) => {
      const line_pts = [];

      for(let i=0; i < data.length; i++) {
        const x = this.xAxis.yearToPix(this.props.sim_days[i].t_yearly);
        const y = this.yAxis.valueToYPix(data[i]);
        const dist = calc_dist(mouse_x, mouse_y, x, y);
        if (dist < select_radius && !selected) {
          selected = true;
          selection.label = label;
          selection.date = this.props.sim_days[i].date;
          selection.y = data[i];
        }
        line_pts.push({ x, y });
      }

      ctx.strokeStyle = calc_color(color, selected);
      ctx.lineWidth = (lineWidth || 1) * (selected ? 1.5 : 1);

      ctx.beginPath();

      for(let i=0; i < line_pts.length; i++) {
        if (i===0) {
          ctx.moveTo(line_pts[i].x, line_pts[i].y);
        } else {
          ctx.lineTo(line_pts[i].x, line_pts[i].y);
        }
      }
      ctx.stroke();
    })

    ctx.restore();

    return selection;
  }

  maybeRenderVertLines = () => {

    const is_selected = (x, y_from, y_to) => {
      const select_radius = 5;

      const mouse_x = this.state.mouse_x;
      const mouse_y = this.state.mouse_y;

      return Math.abs(mouse_x - x) < select_radius &&
        mouse_y >= y_from - select_radius &&
        mouse_y <= y_to + select_radius;
    }

    const canvas = this.refs.canvas;
    const ctx = canvas.getContext("2d");
    const fontSize = 15;
    const lineSize = fontSize + 3;

    ctx.save();
    ctx.clip(this.genClipPathGraph());
    ctx.font = sprintf("%dpx Arial", fontSize);
    ctx.textAlign = "center";
    ctx.strokeStyle = "#FFF";
    ctx.lineWidth = 6;
    ctx.fillStyle = "#000";

    if (this.props.vertLines) {
      let has_selected = false;

      const topAxisHeightPx = this.topAxisHeightPx();
      ctx.lineWidth = 1;
      const date_strs = this.props.vertLines.map(line => line.date);
      const rebalance_ticks = this.xAxis.collectTicks(date_strs);

      this.props.vertLines.forEach(line => {
        const x = this.xAxis.yearToPix(rebalance_ticks[line.date]);
        const y_to = lerp(line.bottom_t, this.yAxis.bottom, topAxisHeightPx);
        const y_from = lerp(line.top_t, this.yAxis.bottom, topAxisHeightPx);
        const sel = is_selected(x, y_from, y_to);
        const width_mult = sel ? 1.5 : 1;

        ctx.strokeStyle = line.color;
        ctx.lineWidth = (line.width || 1) * width_mult;
        ctx.beginPath();
        ctx.moveTo(x, y_from);
        ctx.lineTo(x, y_to);
        ctx.stroke();

        if (sel && !has_selected) {
          ctx.strokeStyle = "#FFF";
          ctx.fillStyle = "#000";
          const text_y = lerp(line.label_t, this.yAxis.bottom, topAxisHeightPx);
          for(let i=0; i < line.labels.length; i++) {
            ctx.strokeText(line.labels[i], x, text_y + (i * lineSize));
            ctx.fillText(line.labels[i], x, text_y + (i * lineSize));
          }
        }

        if (sel) {
          has_selected = true;
        }
      })
    }

    ctx.restore();
  }

  maybeRenderSpans = () => {
    if (!this.props.spans) {
      return;
    }

    const topAxisHeightPx = this.topAxisHeightPx();
    const canvas = this.refs.canvas;
    const ctx = canvas.getContext("2d");

    ctx.save();
    ctx.clip(this.genClipPathGraph());

    this.props.spans.forEach((span, index) => {
      const is_selected = this.state.span_select_0[index];
      const span_ticks = this.xAxis.collectTicks([span.from_date, span.to_date]);
      const span_from_px = this.xAxis.yearToPix(span_ticks[span.from_date]);
      let span_to_px = this.xAxis.yearToPix(span_ticks[span.to_date]);
      if (span_to_px === span_from_px) {
        span_to_px += 2;
      }

      ctx.fillStyle = span.color;
      if (is_selected && span.selected_color) {
        ctx.fillStyle = span.selected_color;
      } else {
        ctx.fillStyle = span.color;
      }
      ctx.fillRect(
          span_from_px, topAxisHeightPx, span_to_px - span_from_px, this.yAxis.bottom - topAxisHeightPx);
    })

    ctx.restore();

    ctx.save();
    ctx.clip(this.genClipPathTopMargin());

    this.props.spans.forEach((span, index) => {
      const is_selected = this.state.span_select_0[index];
      if (is_selected && span.label) {
        const span_ticks = this.xAxis.collectTicks([span.from_date, span.to_date]);
        const span_from_px = this.xAxis.yearToPix(span_ticks[span.from_date]);
        const span_to_px = this.xAxis.yearToPix(span_ticks[span.to_date]);

        this.renderLines([span.label], (span_from_px + span_to_px) / 2, topAxisHeightPx - 4);
        /*ctx.textAlign = "center";
        ctx.fillStyle = "#000";
        ctx.fillText(span.label, (span_from_px + span_to_px) / 2., topAxisHeightPx - 4);*/
      }
    })

    ctx.restore();
  }

  updateSpanSelection = (mouse_x, mouse_y) => {

    if (!this.props.spans) {
      return;
    }

    const span_selection = [];
    let update = false;
    let span_data;
    const select_width = 5;

    this.props.spans.forEach((span, index) => {
      const span_ticks = this.xAxis.collectTicks([span.from_date, span.to_date]);
      const span_from_px = this.xAxis.yearToPix(span_ticks[span.from_date]) - select_width;
      const span_to_px = this.xAxis.yearToPix(span_ticks[span.to_date]) + select_width;

      const selected = mouse_y >= 0 && mouse_y <= this.yAxis.bottom &&
        mouse_x >= span_from_px && mouse_x <= span_to_px;
      span_selection.push(selected);
      if (selected) {
        span_data = span.data;
      }
      if (this.state.span_select_0[index] !== selected) {
        update = true;
      }
    });

    if (update) {
      if (this.props.on_span_selected) {
        this.props.on_span_selected(span_data);
      }
      return span_selection;
    }
    return null;
  }

  renderLines(lines, x, y, fontSize = 15) {
    const line_height = fontSize + 3;
    const canvas = this.refs.canvas;
    const ctx = canvas.getContext("2d");

    ctx.font = sprintf("%dpx Arial", fontSize);
    ctx.textAlign = "center";
    ctx.strokeStyle = "#FFF";
    ctx.lineWidth = 6;
    ctx.fillStyle = "#000";

    lines.forEach(line => {
      ctx.strokeText(line, x, y);
      ctx.fillText(line, x, y);
      y += line_height;
    })
  }

  renderPointer = (selection) => {
    const canvas = this.refs.canvas;
    const ctx = canvas.getContext("2d");
    const x = this.state.mouse_x;
    const y = this.state.mouse_y;

    ctx.strokeStyle = "#000";
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.arc(
      x, y,
      4,
      0, 2 * Math.PI, true);
    ctx.stroke();

    if (selection.label !== "") {
      this.renderLines([
        selection.label,
        selection.date,
        dollarToString(selection.y),
      ], x + 20, y - 60);
    }
  }

  create_date_lut = () => {
    const date_to_t = {};
    this.props.sim_days.forEach(day => {
      date_to_t[day.date] = day.t_yearly;
    });
    return date_to_t;
  }

  updateCanvas = () => {
    const canvas = this.refs.canvas;
    this.date_to_t = this.create_date_lut();

    // THIS is how you reset the HTML canvas?? Lame...
    canvas.width = canvas.width;

    const matrix_inv = DenseMatrix.fromJSON(this.state.view_matrix_inv);
    this.xAxis.render(canvas, matrix_inv);
    const group_index = this.props.group_index;
    this.yAxis.setData(this.props.group_data[group_index].data, this.props.default_y_bounds);
    this.yAxis.render(canvas, matrix_inv);

    this.renderOutline();

    const selection = this.renderData();

    if (this.state.show_time_items) {
      this.maybeRenderVertLines();
      this.maybeRenderSpans();
    }

    this.renderPointer(selection);
  };

  onMouseDown = ev => {
    this.setState({
      mouse_x: ev.nativeEvent.offsetX,
      mouse_y: ev.nativeEvent.offsetY,
      view_op: ev.ctrlKey ? VIEW_OP.SCALE : VIEW_OP.PAN,
    })
    this.grabbed_pos = {
      x: ev.nativeEvent.offsetX,
      y: ev.nativeEvent.offsetY,
    }
    this.view_matrix = DenseMatrix.fromJSON(this.state.view_matrix);
    this.view_matrix_inv = DenseMatrix.fromJSON(this.state.view_matrix_inv);
  }

  onMouseUp = ev => {
    const new_state = {
      mouse_x: ev.nativeEvent.offsetX,
      mouse_y: ev.nativeEvent.offsetY,
      view_op: VIEW_OP.NONE,
    }

    this.setState(new_state);
  }

  onMouseMove = ev => {
    const new_state = {
      mouse_x: ev.nativeEvent.offsetX,
      mouse_y: ev.nativeEvent.offsetY,
    }
    let new_matrix;

    if (this.state.view_op === VIEW_OP.SCALE) {
      new_matrix = mathjs.multiply(
        this.view_matrix,
        this.xAxis.calcScaleMatrix(this.grabbed_pos.x, new_state.mouse_x, this.view_matrix_inv),
        this.yAxis.calcScaleMatrix(this.grabbed_pos.y, new_state.mouse_y, this.view_matrix_inv));

    } else if (this.state.view_op === VIEW_OP.PAN) {
      new_matrix = mathjs.multiply(
        this.view_matrix,
        this.xAxis.calcPanMatrix(this.grabbed_pos.x, new_state.mouse_x, this.view_matrix_inv),
        this.yAxis.calcPanMatrix(this.grabbed_pos.y, new_state.mouse_y, this.view_matrix_inv));
    } else if (this.state.view_op === VIEW_OP.NONE) {
      if (this.state.show_time_items) {
        const new_select = this.updateSpanSelection(ev.nativeEvent.offsetX, ev.nativeEvent.offsetY);
        if (new_select) {
          new_state.span_select_0 = new_select;
        }
      }
    }

    if (new_matrix) {
      new_state.view_matrix = new_matrix.toJSON();
      new_state.view_matrix_inv = mathjs.inv(new_matrix).toJSON();
    }

    this.setState(new_state);
  }

  onMouseLeave = ev => {
    const new_state = {
      view_op: VIEW_OP.NONE,
    }

    this.setState(new_state);
  }

  onKeyDown = ev => {
    if (ev.code === "KeyF") {
      this.setState({
        view_matrix: identity_matrix().toJSON(),
        view_matrix_inv: identity_matrix().toJSON(),
      })
    } else if (ev.code === "KeyT") {
      this.setState({
        show_time_items: !this.state.show_time_items,
      })
    } else if (ev.code === "ArrowRight" && ev.ctrlKey) {
      const group_index = (this.props.group_index + 1)
        % this.props.group_data.length;

      this.props.on_group_selected(group_index);

    } else if (ev.code === "ArrowLeft" && ev.ctrlKey) {
      let group_index = this.props.group_index - 1;
      if (group_index === -1) {
        group_index = this.props.group_data.length - 1;
      }
      this.props.on_group_selected(group_index);
    }
  }

  render = () => {
    return (
      <canvas
        ref="canvas"
        onMouseLeave={this.onMouseLeave}
        onMouseDown={this.onMouseDown}
        onMouseUp={this.onMouseUp}
        onMouseMove={this.onMouseMove}
        width={this.props.width_px}
        height={this.props.height_px}/>
    );
  }
}

export { ChartRt };
